import { ITextIterator, ReadingAtom } from './interfaces';
import { ReadingUnitType } from './enums';
import { HtmlTextWalker, ITxtmapItem, INodeDescription } from '../html-and-text-utils';

export class HtmlTextIterator implements ITextIterator {
    private readonly htmlWalker: HtmlTextWalker | null;

    private wordIdx: number = 0;

    public readonly htmlTextRoot: Node | null;

    constructor(public readonly actvSlctn: Selection | null, private readonly htmlRootBndry: string | null = null) {
        if (this.actvDocument) {
            this.htmlTextRoot = this.htmlRootBndry !== null ? this.actvDocument.querySelector(this.htmlRootBndry) : this.actvDocument.body;
            if (this.htmlTextRoot) {
                this.htmlWalker = new HtmlTextWalker(this.htmlTextRoot, this.actvDocument);
            }
        }
    }

    get actvDocument(): Document | null {
        if (this.actvSlctn && this.actvSlctn.anchorNode !== null) {
            return this.actvSlctn.anchorNode.ownerDocument;
        }
        return null;
    }

    // TODO: move punctuation filter to settings
    getNextItem(itemType: ReadingUnitType): ReadingAtom[] | null {
        let items: ITxtmapItem[] = [];
        if (this.htmlWalker === null) {
            return null;
        }

        while (items.length === 0 && this.wordIdx < this.htmlWalker.wordsNum) {
            items = this.htmlWalker.getNextUnit(this.wordIdx, itemType);
            this.wordIdx = items.length > 0 ? items[0].itemIndex : this.wordIdx + 1;
        }

        return this.createReadingItem(items);
    }

    getPrevItem(itemType: ReadingUnitType): ReadingAtom[] | null {
        if (this.htmlWalker === null) { return null; }
        const items = this.htmlWalker.getPrevUnit(this.wordIdx, itemType);
        this.wordIdx = items[0].itemIndex;
        return this.createReadingItem(items);
    }

    getFirstItem(itemType: ReadingUnitType): ReadingAtom[] | null {
        if (this.htmlWalker === null) { return null; }
        const items = itemType === ReadingUnitType.Sentence ? this.htmlWalker.getSentenceByIdx(0) : [this.htmlWalker.getMapItemByIdx(0)];
        return this.createReadingItem(items);
    }

    getLastItem(itemType: ReadingUnitType): ReadingAtom[] | null {
        if (this.htmlWalker === null) { return null; }

        let items: ITxtmapItem[] = [];

        if (itemType === ReadingUnitType.Sentence) {
            items = this.htmlWalker.getSentenceByIdx(this.htmlWalker.sentencesNum);
        } else {
            const word = this.htmlWalker.getLastWord();
            items = word ? [word] : [];
        }

        return this.createReadingItem(items);
    }

    getClosestItemToCursor(itemType: ReadingUnitType, storeItemPos: boolean = true): ReadingAtom[] | null {
        if (this.htmlWalker === null || this.actvSlctn === null) { return null; }
        if (this.actvSlctn.anchorNode === null) { return null; }
        const items = this.htmlWalker.getClosestItemsByNode(
            this.actvSlctn.anchorNode, this.actvSlctn.anchorOffset, itemType
        );

        if (!items || items.length && items[0] === null) {
            return null;
        }

        if (storeItemPos) {
            this.wordIdx = items[0].itemIndex;
        }

        return this.createReadingItem(items);
    }

    getItemUnderCursor(itemType: ReadingUnitType, storeItemPos: boolean = true): ReadingAtom[] | null {
        if (this.htmlWalker === null || this.actvSlctn === null) { return null; }
        if (this.actvSlctn.anchorNode === null) { return null; }

        const items = this.htmlWalker.getItemsByNode(
            this.actvSlctn.anchorNode, this.actvSlctn.anchorOffset, itemType
        );

        if (!items || items.length && items[0] === null) {
            return null;
        }

        if (storeItemPos) {
            this.wordIdx = items[0].itemIndex;
        }

        return this.createReadingItem(items);
    }

    getItemBeforeCursor(itemType: ReadingUnitType): ReadingAtom[] | null {
        if (this.htmlWalker === null || this.actvSlctn === null) { return null; }
        if (this.actvSlctn.anchorNode === null) { return null; }

        let offset = this.actvSlctn.anchorOffset - 1;
        let node = this.actvSlctn.anchorNode;

        if (offset < 0) {
            const prev = this.getPrevTextNodeAndOffsetBeforeCursor(node);
            if (!prev) { return null; }
            node = prev.node;
            offset = prev.offset;
        }

        if (this.actvSlctn.anchorNode.nodeName !== '#text') {
            for (let i = this.actvSlctn.anchorOffset - 1; i >=0; i--) {
                if (this.actvSlctn.anchorNode.childNodes[i].nodeName === '#text') {
                    offset = (this.actvSlctn.anchorNode.childNodes[i] as Text).length - 1;
                    node = this.actvSlctn.anchorNode.childNodes[i];
                    break;
                }
            }
        }
        const items = this.htmlWalker.getItemsByNode(node, offset, itemType);

        if (!items || items.length && items[0] === null) {
            return null;
        }
        this.wordIdx = items[0].itemIndex;

        return this.createReadingItem(items);
    }

    getNextSentenceFrom(itemIdx: number): ReadingAtom[] | null {
        if (this.htmlWalker === null) { return null; }
        // TODO: move punctuation filter to settings
        const items = this.htmlWalker.getSentencePart(itemIdx);
        this.wordIdx = itemIdx;
        return this.createReadingItem(items);
    }

    getRangeForWords(words: ReadingAtom[]): Range | null {
        if (this.htmlWalker === null) { return null; }
        let crntRange: Range | null = null;

        if (words.length === 1) {
            crntRange = this.htmlWalker.getWordRange(words[0].itemIdx);
        } else if (words.length > 1) {
            crntRange = this.htmlWalker.getSentenceRange(words[0].itemIdx, words[words.length - 1].itemIdx);
        }

        return crntRange;
    }

    getRangeForUnit(startWordIdx: number, itemType: ReadingUnitType): Range | null {
        if (this.htmlWalker === null) { return null; }
        if (itemType === ReadingUnitType.Word) {
            return this.htmlWalker.getWordRange(startWordIdx);
        } else {
            const word = this.htmlWalker.getMapItemByIdx(startWordIdx);
            const sentence = this.htmlWalker.getSentenceByIdx(word.sntncIndex);
            return this.htmlWalker.getSentenceRange(sentence[0].itemIndex, sentence[sentence.length - 1].itemIndex);
        }
    }

    getWordByPoint(x: number, y: number): ReadingAtom[] | null {
        if (this.htmlWalker === null) { return null; }
        const item = this.htmlWalker.getItemByPoint(x, y);
        return item !== null ? this.createReadingItem([item]) : null;
    }

    getWordLang(wordIdx: number): string {
        if (this.htmlWalker === null) { return ''; }
        const word = this.htmlWalker.getMapItemByIdx(wordIdx);
        return word !== null ? word.lang : '';
    }

    getReadingItemNode(wordIdx: number): INodeDescription[] | null {
        if (this.htmlWalker === null) { return null; }
        const mappedItem = this.htmlWalker.getMapItemByIdx(wordIdx);
        return this.htmlWalker.getWordTextNodes(mappedItem);
    }

    // TODO: move punctuation filter to settings
    getWordsFromSelection(): ReadingAtom[] | null {
        if (this.htmlWalker === null) { return null; }

        if (this.actvSlctn) {
            const itms = this.htmlWalker.getWordsFromRange(this.actvSlctn.getRangeAt(0));
            return this.createReadingItem(itms);
        }

        return null;
    }

    private createReadingItem(items: ITxtmapItem[] | null): ReadingAtom[] | null {
        if (items === null || items.length === 0) {
            return null;
        }
        if (this.htmlWalker) {
            return items.map((item: ITxtmapItem, idx: number) => {
                return {
                    symbPos: item.symbPos,
                    itemIdx: item.itemIndex,
                    sntncIndex : item.sntncIndex,
                    itemType: item.type,
                    isLast: this.htmlWalker ? idx === this.htmlWalker.wordsNum - 1 : false,
                    text: item.content, lang: '',
                    boundary: null,
                    pageIdx: 0,
                };
            });
        }
        return null;
    }

    private getPrevTextNodeAndOffsetBeforeCursor(start: Node): { node: Node; offset: number } | null {
        let el: Node = start.nodeType === Node.TEXT_NODE ? start.parentNode! : start;
        let prev: Node | null = el.previousSibling;

        while (prev && prev.nodeType !== Node.ELEMENT_NODE) {
            prev = prev.previousSibling;
        }
        if (!prev) return null;

        const lastText = this.findLastTextNode(prev);
        if (!lastText) return null;

        return {
            node: lastText,
            offset: (lastText as Text).length - 1
        };
    }

    private findLastTextNode(root: Node): Node | null {
        if (root.nodeType === Node.TEXT_NODE) {
            return root;
        }
        for (let i = root.childNodes.length - 1; i >= 0; i--) {
            const found = this.findLastTextNode(root.childNodes[i]);
            if (found) return found;
        }
        return null;
    }
}
